import React from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpenIcon,
  LanguageIcon,
  TrophyIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import { ScrollTextIcon } from "lucide-react";

const menuItems = [
  {
    id: "Belajar",
    name: "Belajar",
    path: "/learn",
    icon: <BookOpenIcon className="w-5 h-5" />,
  },
  {
    id: "Aksara",
    name: "Aksara",
    path: "/aksara",
    icon: (
      <span
        className="text-lg w-5 h-5 flex items-center justify-center font-bold pb-1"
        style={{ fontFamily: "Noto Sans Batak" }}
      >
        ᯀ
      </span>
    ),
  },
  {
    id: "Terjemahan",
    name: "Terjemahan",
    path: "/terjemahan",
    icon: <LanguageIcon className="w-5 h-5" />,
  },
  {
    id: "Sastra & Budaya",
    name: "Sastra & Budaya",
    path: "/sastra-budaya",
    icon: <ScrollTextIcon className="w-5 h-5" />,
  },
  {
    id: "Pencapaian",
    name: "Pencapaian",
    path: "/achievement",
    icon: <TrophyIcon className="w-5 h-5" />,
  },
  {
    id: "Pengaturan",
    name: "Pengaturan",
    path: "/settings",
    icon: <Cog6ToothIcon className="w-5 h-5" />,
  },
];

interface LeftSidebarProps {
  currentSection: string;
  setCurrentSection: (id: string) => void;
  darkMode?: boolean;
  compact?: boolean;
}

const LeftSidebar: React.FC<LeftSidebarProps> = ({
  currentSection,
  setCurrentSection,
  darkMode = false,
  compact = false,
}) => {
  const navigate = useNavigate();

  const handleMenuClick = (item: (typeof menuItems)[0]) => {
    setCurrentSection(item.id);
    if (item.path) {
      navigate(item.path);
    }
  };

  if (compact) {
    // Tablet: icon-only sidebar, narrow width, keep Aksa icon at top
    return (
      <div
        className={`w-16 shadow-lg sticky top-0 h-screen z-20 flex flex-col overflow-y-auto items-center py-4 ${
          darkMode ? "bg-gray-800 border-r border-gray-700" : "bg-gray-100"
        }`}
      >
        {/* Aksa icon at top */}
        <img
          src="/assets/aksa-icon-square.png"
          alt="Aksa Logo"
          className="w-10 h-10 mb-6"
        />
        <nav className="flex flex-col gap-4 mt-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleMenuClick(item)}
              className={`w-12 h-12 flex items-center justify-center rounded-lg transition-colors ${
                item.id === currentSection
                  ? darkMode
                    ? "bg-blue-900/30 text-blue-300 border-2 border-blue-700"
                    : "bg-blue-50 text-blue-600 border-2 border-blue-200"
                  : darkMode
                  ? "text-gray-300 hover:bg-gray-700/50"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              aria-label={item.name}
            >
              {item.icon}
            </button>
          ))}
        </nav>
      </div>
    );
  }
  // Desktop: full sidebar
  return (
    <div
      className={`w-64 shadow-lg sticky top-0 h-screen z-20 overflow-y-auto ${
        darkMode ? "bg-gray-800 border-r border-gray-700" : "bg-gray-100"
      }`}
    >
      <div className="p-6">
        <div className="flex items-center space-x-3 mb-8">
          <img
            src="/assets/aksa-icon-square.png"
            alt="Aksa Logo"
            className="w-16 h-16"
          />
          <div style={{ fontFamily: "Feather Bold" }}>
            <h1
              className="text-3xl font-bold"
              style={{ color: darkMode ? "#fff" : "#1A365D" }}
            >
              Galaxy
            </h1>
            <h2
              className="text-3xl font-bold"
              style={{ color: darkMode ? "#EF4444" : "#EF4444" }}
            >
              Aksara
            </h2>
          </div>
        </div>
        <nav className="space-y-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleMenuClick(item)}
              className={`w-full text-left px-4 py-3 rounded-lg flex items-center space-x-3 transition-colors ${
                item.id === currentSection
                  ? darkMode
                    ? "bg-blue-900/30 text-blue-300 border-2 border-blue-700"
                    : "bg-blue-50 text-blue-600 border-2 border-blue-200"
                  : darkMode
                  ? "text-gray-300 hover:bg-gray-700/50"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              {/* Icon */}
              <span>{item.icon}</span>
              <span className="font-medium">{item.name}</span>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default LeftSidebar;
